/**
 * Client-side mirror of backend's scoring logic.
 * Used for OPTIMISTIC UI when the request goes into the offline queue.
 * On reconnect, the server response replaces the optimistic state.
 */

const isLegalDelivery = (extras_type) => !["wide", "no_ball"].includes(extras_type);

function maxInningsFor(fmt) {
  return fmt === "Test" ? 4 : 2;
}

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function computeInningsSummary(innings) {
  let runs = 0, wickets = 0, legal_balls = 0, extras_total = 0;
  for (const b of innings.balls) {
    runs += (b.runs || 0) + (b.extras_runs || 0);
    extras_total += (b.extras_runs || 0);
    if (b.is_wicket) wickets += 1;
    if (isLegalDelivery(b.extras_type)) legal_balls += 1;
  }
  const overs_completed = Math.floor(legal_balls / 6);
  const balls_in_over = legal_balls % 6;
  return {
    runs,
    wickets,
    legal_balls,
    overs: `${overs_completed}.${balls_in_over}`,
    overs_completed,
    balls_in_over,
    extras: extras_total,
  };
}

function computeBatsmanStats(innings, player_id) {
  let runs = 0, balls = 0, fours = 0, sixes = 0, out = false;
  for (const b of innings.balls) {
    if (b.striker_id === player_id) {
      if (b.extras_type === "none" || b.extras_type === "no_ball") {
        runs += b.runs || 0;
        if (b.runs === 4) fours += 1;
        else if (b.runs === 6) sixes += 1;
      }
      if (b.extras_type !== "wide") balls += 1;
    }
    if (b.is_wicket && (b.out_player_id || b.striker_id) === player_id) out = true;
  }
  const sr = balls > 0 ? Math.round((runs / balls) * 100 * 100) / 100 : 0;
  return { player_id, runs, balls, fours, sixes, out, strike_rate: sr };
}

function computeBowlerStats(innings, bowler_id) {
  let legal_balls = 0, runs_conceded = 0, wickets = 0;
  for (const b of innings.balls) {
    if (b.bowler_id === bowler_id) {
      runs_conceded += b.runs || 0;
      if (b.extras_type === "wide" || b.extras_type === "no_ball") {
        runs_conceded += b.extras_runs || 0;
      }
      if (isLegalDelivery(b.extras_type)) legal_balls += 1;
      if (b.is_wicket && b.wicket_type !== "run_out") wickets += 1;
    }
  }
  const overs = Math.floor(legal_balls / 6);
  const bib = legal_balls % 6;
  const econ = legal_balls > 0 ? Math.round((runs_conceded / (legal_balls / 6)) * 100) / 100 : 0;
  return {
    player_id: bowler_id,
    overs: `${overs}.${bib}`,
    legal_balls,
    runs: runs_conceded,
    wickets,
    economy: econ,
  };
}

export function buildFullMatch(match) {
  const teams = match.teams;
  const innings_with_summary = match.innings.map((inn) => {
    const summary = computeInningsSummary(inn);
    const batting_team = teams[inn.batting_team_idx];
    const bowling_team = teams[inn.bowling_team_idx];

    const batsmen_stats = batting_team.players.map((p) => {
      const stats = computeBatsmanStats(inn, p.id);
      const faced = inn.balls.some(
        (b) => b.striker_id === p.id || b.non_striker_id === p.id,
      );
      return { ...stats, name: p.name, did_not_bat: !faced };
    });

    const bowler_ids_seen = [];
    for (const b of inn.balls) {
      if (!bowler_ids_seen.includes(b.bowler_id)) bowler_ids_seen.push(b.bowler_id);
    }
    const bowler_stats = bowler_ids_seen.map((bid) => {
      const bp = bowling_team.players.find((p) => p.id === bid);
      const stats = computeBowlerStats(inn, bid);
      return { ...stats, name: bp ? bp.name : "Unknown" };
    });

    return {
      ...inn,
      summary,
      batting_team_name: batting_team.name,
      bowling_team_name: bowling_team.name,
      batsmen_stats,
      bowler_stats,
    };
  });
  return { ...match, innings: innings_with_summary };
}

function aggregateRunsForTeam(match, team_idx, up_to_idx) {
  if (up_to_idx < 0) return 0;
  let total = 0;
  for (let i = 0; i <= Math.min(up_to_idx, match.innings.length - 1); i++) {
    const inn = match.innings[i];
    if (inn.batting_team_idx === team_idx) {
      total += computeInningsSummary(inn).runs;
    }
  }
  return total;
}

function finalizeByAggregate(match) {
  const runsA = aggregateRunsForTeam(match, 0, match.innings.length - 1);
  const runsB = aggregateRunsForTeam(match, 1, match.innings.length - 1);
  match.status = "completed";
  if (runsA === runsB) {
    match.result = "Match Tied";
    match.winner_team_idx = null;
  } else {
    const winner_idx = runsA > runsB ? 0 : 1;
    const diff = Math.abs(runsA - runsB);
    match.winner_team_idx = winner_idx;
    match.result = `${match.teams[winner_idx].name} won by ${diff} runs`;
  }
}

function newInnings(batting_team_idx, bowling_team_idx) {
  return {
    batting_team_idx,
    bowling_team_idx,
    balls: [],
    striker_id: null,
    non_striker_id: null,
    bowler_id: null,
    out_player_ids: [],
    started: false,
    completed: false,
  };
}

/**
 * Apply a record-ball to a clone of the match and return the new full-match.
 * Mirrors backend's `record_ball` logic.
 */
export function recordBallLocal(originalMatch, req) {
  const match = deepClone(originalMatch);
  if (match.status !== "live") return buildFullMatch(match);
  const inn_idx = match.current_innings;
  const inn = match.innings[inn_idx];
  if (!inn.started || inn.completed) return buildFullMatch(match);

  const summary = computeInningsSummary(inn);
  const over_num = summary.overs_completed;
  const ball_num = summary.balls_in_over + 1;

  let extras_runs = 0;
  let bat_runs = 0;
  const runs = Number(req.runs) || 0;
  const extras_type = req.extras_type || "none";
  if (extras_type === "wide") {
    extras_runs = 1 + runs;
    bat_runs = 0;
  } else if (extras_type === "no_ball") {
    extras_runs = 1;
    bat_runs = runs;
  } else if (extras_type === "bye" || extras_type === "leg_bye") {
    extras_runs = runs;
    bat_runs = 0;
  } else {
    bat_runs = runs;
  }

  const out_player_id = req.out_player_id || inn.striker_id;

  const ball = {
    over_num,
    ball_num,
    striker_id: inn.striker_id,
    non_striker_id: inn.non_striker_id,
    bowler_id: inn.bowler_id,
    runs: bat_runs,
    extras_type,
    extras_runs,
    is_wicket: !!req.is_wicket,
    wicket_type: req.is_wicket ? req.wicket_type : null,
    out_player_id: req.is_wicket ? out_player_id : null,
    timestamp: new Date().toISOString(),
  };
  inn.balls.push(ball);

  // Strike rotation on odd runs
  let rot_runs = bat_runs;
  if (extras_type === "bye" || extras_type === "leg_bye") rot_runs = runs;
  if (rot_runs % 2 === 1) {
    [inn.striker_id, inn.non_striker_id] = [inn.non_striker_id, inn.striker_id];
  }

  // Wicket handling
  if (req.is_wicket) {
    inn.out_player_ids.push(out_player_id);
    if (req.new_batsman_id) {
      if (out_player_id === inn.striker_id) inn.striker_id = req.new_batsman_id;
      else inn.non_striker_id = req.new_batsman_id;
    } else {
      if (out_player_id === inn.striker_id) inn.striker_id = null;
      else inn.non_striker_id = null;
    }
  }

  // End of over
  const new_summary = computeInningsSummary(inn);
  if (
    new_summary.balls_in_over === 0 &&
    new_summary.legal_balls > summary.legal_balls
  ) {
    if (inn.striker_id && inn.non_striker_id) {
      [inn.striker_id, inn.non_striker_id] = [inn.non_striker_id, inn.striker_id];
    }
    inn.bowler_id = null;
  }

  // Innings end conditions
  const batting_team = match.teams[inn.batting_team_idx];
  const total_players = batting_team.players.length;
  const all_out = inn.out_player_ids.length >= total_players - 1;
  const overs_done = match.format !== "Test" && new_summary.overs_completed >= match.overs;

  const fmt = match.format;
  const max_inn = maxInningsFor(fmt);
  let target_chased = false;
  if (inn_idx === max_inn - 1) {
    const batting_idx = inn.batting_team_idx;
    const opposing_idx = 1 - batting_idx;
    const own_prior = aggregateRunsForTeam(match, batting_idx, inn_idx - 1);
    const opposing_total = aggregateRunsForTeam(match, opposing_idx, inn_idx - 1);
    if (own_prior + new_summary.runs > opposing_total) target_chased = true;
  }

  if (all_out || overs_done || target_chased) {
    inn.completed = true;
    if (target_chased || inn_idx === max_inn - 1) {
      if (target_chased) {
        match.status = "completed";
        match.winner_team_idx = inn.batting_team_idx;
        const wickets_left = total_players - 1 - inn.out_player_ids.length;
        match.result = `${match.teams[inn.batting_team_idx].name} won by ${wickets_left} wickets`;
      } else {
        finalizeByAggregate(match);
      }
    } else {
      match.innings.push(newInnings(inn.bowling_team_idx, inn.batting_team_idx));
      match.current_innings = inn_idx + 1;
    }
  }

  match.updated_at = new Date().toISOString();
  return buildFullMatch(match);
}

/**
 * Apply undo locally. Mirrors backend's undo_ball.
 */
export function undoBallLocal(originalMatch) {
  const match = deepClone(originalMatch);
  let inn_idx = match.current_innings;
  let inn = match.innings[inn_idx];

  if (inn.balls.length === 0 && inn_idx > 0) {
    match.innings.pop();
    match.current_innings = inn_idx - 1;
    inn_idx = match.current_innings;
    inn = match.innings[inn_idx];
    inn.completed = false;
  }
  if (inn.balls.length === 0) return buildFullMatch(match);

  const last = inn.balls.pop();
  inn.completed = false;
  inn.out_player_ids = inn.balls
    .filter((b) => b.is_wicket && b.out_player_id)
    .map((b) => b.out_player_id);
  inn.striker_id = last.striker_id;
  inn.non_striker_id = last.non_striker_id;
  inn.bowler_id = last.bowler_id;
  match.status = "live";
  match.result = null;
  match.winner_team_idx = null;
  match.updated_at = new Date().toISOString();
  return buildFullMatch(match);
}

/**
 * Apply set-bowler locally.
 */
export function setBowlerLocal(originalMatch, bowler_id) {
  const match = deepClone(originalMatch);
  const inn = match.innings[match.current_innings];
  inn.bowler_id = bowler_id;
  match.updated_at = new Date().toISOString();
  return buildFullMatch(match);
}

/**
 * Apply edit-ball locally.
 */
export function editBallLocal(originalMatch, innings_idx, ball_idx, req) {
  const match = deepClone(originalMatch);
  const inn = match.innings[innings_idx];
  if (!inn || ball_idx < 0 || ball_idx >= inn.balls.length) return buildFullMatch(match);
  const b = inn.balls[ball_idx];

  let extras_runs = 0, bat_runs = 0;
  const runs = Number(req.runs) || 0;
  if (req.extras_type === "wide") { extras_runs = 1 + runs; bat_runs = 0; }
  else if (req.extras_type === "no_ball") { extras_runs = 1; bat_runs = runs; }
  else if (req.extras_type === "bye" || req.extras_type === "leg_bye") { extras_runs = runs; bat_runs = 0; }
  else { extras_runs = 0; bat_runs = runs; }

  b.runs = bat_runs;
  b.extras_type = req.extras_type;
  b.extras_runs = extras_runs;
  b.is_wicket = !!req.is_wicket;
  b.wicket_type = req.is_wicket ? req.wicket_type : null;
  b.out_player_id = req.is_wicket ? (req.out_player_id || b.striker_id) : null;

  inn.out_player_ids = inn.balls
    .filter((bb) => bb.is_wicket && bb.out_player_id)
    .map((bb) => bb.out_player_id);
  match.updated_at = new Date().toISOString();
  return buildFullMatch(match);
}
